import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToastsContext } from './ToastContext';
import { Id, ToastPlacement, ToastType, ToastsType } from './types';

export const generateUEID = () => {
  const first = ('000' + ((Math.random() * 46656) | 0).toString(36)).slice(-3);
  const second = ('000' + ((Math.random() * 46656) | 0).toString(36)).slice(-3);
  return `${first}${second}`;
};

// Pauses closing the toast when the mouse is over the toast
export const useTimeout = (fn: (...args: any) => void, duration: number) => {
  const [isRunning, setIsRunning] = useState(true);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  useEffect(() => {
    if (isRunning) {
      const timerId = setTimeout(() => {
        fn();
      }, duration);

      return () => clearTimeout(timerId);
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  return {
    start,
    stop,
  };
};

// This is the main hook that the ToastProvider uses. You should normally use infoToast or
// errorToast from the useToast hook below, instead of this one.
export function useToasts() {
  const [toasts, setToasts] = useState<ToastsType>([]);
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const [placements, setPlacements] = useState(new Set<ToastPlacement>([]));

  const toast = useCallback(
    ({
      placement,
      delay,
      transition,
      autoClose,
      text,
      background,
      color,
      closeable,
      render,
    }: Omit<ToastType, 'id'>) => {
      setPlacements((prev) => {
        if (!placement) {
          return new Set(prev);
        }

        const placements = new Set(prev);
        placements.add(placement);
        return placements;
      });
      setToasts((prev) => [
        ...prev,
        {
          id: generateUEID(),
          placement,
          delay,
          transition,
          autoClose,
          text,
          background,
          color,
          closeable,
          render,
        },
      ]);
    },
    [],
  );

  const close = useCallback((toastId: Id) => {
    setToasts((prev) => {
      const t = prev.map((toast) => {
        if (toast.id === toastId) {
          return {
            ...toast,
            requestClose: true,
          };
        }
        return toast;
      });
      return t;
    });
  }, []);

  const closeAll = useCallback(() => {
    const t = toasts.map((toast) => ({
      ...toast,
      requestClose: true,
    }));
    setToasts(t);
  }, [toasts]);

  return useMemo(
    () => ({
      listRef,
      toasts,
      placements,
      toast,
      close,
      closeAll,
    }),
    [listRef, toasts, placements, toast, close, closeAll],
  );
}

/**
 * This hook returns an object with `infoToast` and `errorToast` functions for showing brief,
 * informational toast messages. The page must have a ToastProvider component wrapping it in
 * order to use this hook.
 *
 * Usage:
 * ```tsx
 * import { useToast } from './hooks';
 *
 * export function Toolbar() {
 *   const { infoToast, errorToast } = useToast();
 *
 *   return (
 *     <button
 *       onClick={() => {
 *         saveBookmark()
 *           .then(() => {
 *             infoToast('Bookmark saved!');
 *           })
 *           .catch(() => {
 *             errorToast('Failed to save bookmark');
 *           });
 *       }}
 *     >
 *       Bookmark
 *     </button>
 *   );
 * }
 * ```
 */
export const useToast = () => {
  const context = useToastsContext();
  if (context == null) {
    throw new Error('Toast components must be wrapped in <ToastProvider />');
  }

  const toast = context.toast;

  const infoToast = useCallback(
    (text: string) => {
      toast({
        text,
      });
    },
    [toast],
  );

  const errorToast = useCallback(
    (text: string) => {
      toast({
        text,
        background: 'var(--error-dark, #b60000)',
        color: 'var(--text-white, #fff)',
      });
    },
    [toast],
  );

  return {
    toast,
    infoToast,
    errorToast,
    close: context.close,
    toasts: context.toasts,
    closeAll: context.closeAll,
  };
};
